import { IMPACT_COLUMN_EXPERIENCE_META } from "./columnExperience";
import { columnImageUrls, type ImpactColumnExperience, type ImpactColumnExperienceType } from "./columnExperience";
import {
  DEFAULT_IMPACT_ANCHOR_CUBES,
  ensureDefaultImpactCubes,
  LEGACY_RECIPIENT_MEDIA_COLUMN_IDS,
  type AnchorCube,
  type AnchorCubeColumn,
} from "./anchorCubes";
import { isImpactCardType } from "./impactCardType";

export const IMPACT_ANCHOR_FLAG = "__impact_anchor";
export const IMPACT_SUBPAGE_FLAG = "__impact_subpage";

export type ImpactApiPage = {
  id?: number;
  order?: number;
  type?: string;
  page_id?: number;
  parent_id?: number;
  parent_page_id?: number;
  tray_id?: number;
  ref_id?: number;
  verified?: boolean;
  is_verified?: boolean;
  status?: string;
  verification_status?: string;
  creator?: string;
  content?: Record<string, unknown> | null;
  sub_pages?: ImpactApiPage[] | null;
  __synthetic_cover?: boolean;
};

export type ImpactPageReviewStatus = "pending" | "Verified" | "Rejected";

export function readImpactPageReviewStatus(page?: ImpactApiPage | null): ImpactPageReviewStatus {
  const content = (page?.content ?? {}) as Record<string, unknown>;
  const raw =
    page?.status ??
    page?.verification_status ??
    content.status ??
    content.verification_status ??
    page?.verified ??
    page?.is_verified ??
    content.verified ??
    content.is_verified;
  const key = String(raw ?? "").trim().toLowerCase();
  if (key === "rejected" || key === "rrejected") return "Rejected";
  if (
    raw === true ||
    raw === 1 ||
    key === "true" ||
    key === "verified" ||
    key === "approved"
  ) {
    return "Verified";
  }
  return "pending";
}

export function isImpactPageVerified(page?: ImpactApiPage | null): boolean {
  return readImpactPageReviewStatus(page) === "Verified";
}

export type AddMainCardPageEntry = {
  order: number;
  type: string;
  content: Record<string, unknown>;
  tray_id: number;
  sub_pages: ImpactApiPage[];
  parent_id?: number;
};

export type AddMainCardPagesPayload = {
  main_card_id: number;
  pages: AddMainCardPageEntry[];
};

export function columnAnchorDomId(columnId: string) {
  return `impact-column-${columnId}`;
}

export function scrollImpactBookToTop(): boolean {
  const scroller = document.querySelector<HTMLElement>("[data-impact-book-scroll='active']");
  if (!scroller) return false;
  scroller.scrollTo({ top: 0, behavior: "smooth" });
  return true;
}

export function scrollImpactColumnIntoView(columnId: string): boolean {
  const scroller = document.querySelector<HTMLElement>("[data-impact-book-scroll='active']");
  if (columnId === "president-thank-you-letter" && scroller) {
    const height = scroller.clientHeight;
    if (height > 0) {
      scroller.scrollTo({ top: height, behavior: "smooth" });
      return true;
    }
  }
  if (columnId === "thanks-aol-gratitude" && scroller) {
    scroller.scrollTo({ top: 0, behavior: "smooth" });
    return true;
  }

  const el = document.getElementById(columnAnchorDomId(columnId));
  if (el && scroller) {
    const top =
      scroller.scrollTop +
      (el.getBoundingClientRect().top - scroller.getBoundingClientRect().top);
    scroller.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    return true;
  }
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    return true;
  }
  return false;
}

export function isImpactAnchorPage(page?: ImpactApiPage | null): boolean {
  const content = (page?.content ?? {}) as Record<string, unknown>;
  if (content[IMPACT_ANCHOR_FLAG] === true) return true;
  if (content[IMPACT_SUBPAGE_FLAG] === true || content.column_id) return false;
  if (parentPageIdOf(page)) return false;
  return Boolean(content.cube_id);
}

export function isImpactSubpage(page?: ImpactApiPage | null): boolean {
  const content = (page?.content ?? {}) as Record<string, unknown>;
  if (content[IMPACT_SUBPAGE_FLAG] === true || content.column_id) return true;
  if (content[IMPACT_ANCHOR_FLAG] === true) return false;
  return Boolean(parentPageIdOf(page));
}

export function isSyntheticCoverPage(page?: ImpactApiPage | null): boolean {
  if (!page) return false;
  if (page.__synthetic_cover === true) return true;
  const content = contentOf(page);
  const id = Number(page.id);
  const contentId = Number(content.id);
  const refId = Number(page.ref_id);
  const order = Number(page.order);
  return order === 0 && id > 0 && (id === contentId || (refId > 0 && id === refId));
}

export function pageIdOf(page?: ImpactApiPage | null): number | undefined {
  if (isSyntheticCoverPage(page)) return undefined;
  const id = Number(page?.page_id ?? page?.id);
  return Number.isFinite(id) && id > 0 ? id : undefined;
}

export function parentPageIdOf(page?: ImpactApiPage | null): number | undefined {
  const content = (page?.content ?? {}) as Record<string, unknown>;
  const parentId = Number(
    page?.parent_id ?? page?.parent_page_id ?? content.parent_page_id ?? content.parent_id
  );
  return Number.isFinite(parentId) && parentId > 0 ? parentId : undefined;
}

/** Backend may return children nested under `sub_pages` or as a flat list with `parent_page_id`. */
export function flattenImpactPages(pages?: ImpactApiPage[] | null): ImpactApiPage[] {
  const out: ImpactApiPage[] = [];
  const visit = (list: ImpactApiPage[], parentId?: number) => {
    for (const page of list ?? []) {
      if (!page) continue;
      const nested = Array.isArray(page.sub_pages) ? page.sub_pages : [];
      const content = contentOf(page);
      const inferredParent = parentId ?? parentPageIdOf(page);
      out.push({
        ...page,
        parent_id: page.parent_id ?? (parentId ? inferredParent : undefined),
        content:
          inferredParent && parentId
            ? {
                ...content,
                [IMPACT_SUBPAGE_FLAG]: content[IMPACT_SUBPAGE_FLAG] ?? true,
                parent_id: content.parent_id ?? inferredParent,
              }
            : content,
        sub_pages: undefined,
      });
      if (nested.length) visit(nested, pageIdOf(page) || inferredParent);
    }
  };
  visit(pages ?? []);
  return out;
}

export function nextPageOrder(pages: ImpactApiPage[]): number {
  let maxOrder = 0;
  for (const page of pages) {
    const order = Number(page?.order);
    if (Number.isFinite(order) && order > maxOrder) maxOrder = order;
  }
  return maxOrder > 0 ? maxOrder + 1 : 1;
}

export function findImpactShellPageId(pages: ImpactApiPage[], impactContentId?: number): number | undefined {
  const shell = pages.find(
    (page) => isImpactCardType(page?.type) && !isImpactAnchorPage(page) && !isImpactSubpage(page)
  );
  const fromShell = pageIdOf(shell);
  if (fromShell) return fromShell;

  const fromCard = Number(impactContentId);
  if (Number.isFinite(fromCard) && fromCard > 0 && pages.some((page) => pageIdOf(page) === fromCard)) {
    return fromCard;
  }

  const lockedCubeIds = new Set(DEFAULT_IMPACT_ANCHOR_CUBES.map((cube) => cube.id));
  for (const page of pages) {
    const cubeId = String(contentOf(page).cube_id || "");
    if (!lockedCubeIds.has(cubeId)) continue;
    const parentId = parentPageIdOf(page);
    if (parentId) return parentId;
  }

  const anyImpactPage = pages.find((page) => isImpactCardType(page?.type) && pageIdOf(page));
  return pageIdOf(anyImpactPage);
}

export function findAnchorPageIdByCubeId(pages: ImpactApiPage[], cubeId: string): number | undefined {
  const match = pages.find((page) => {
    if (!isImpactAnchorPage(page)) return false;
    const content = (page.content ?? {}) as Record<string, unknown>;
    return String(content.cube_id || "") === cubeId;
  });
  return pageIdOf(match);
}

function isKnownPageId(pages: ImpactApiPage[], id?: number): id is number {
  if (!id || id <= 0) return false;
  return pages.some((page) => pageIdOf(page) === id);
}

/** Parent API page for a new subpage. Only a real main_card_pages id is valid. */
export function resolveSubpageParentPageId(
  cube: { id: string; pageId?: number; locked?: boolean },
  pages: ImpactApiPage[],
  _impactContentId?: number
): number | undefined {
  if (cube.locked) return undefined;
  const fromCube = Number(cube.pageId);
  if (isKnownPageId(pages, fromCube)) return fromCube;
  const fromAnchor = findAnchorPageIdByCubeId(pages, cube.id);
  if (isKnownPageId(pages, fromAnchor)) return fromAnchor;
  return undefined;
}

export function experienceToPageType(type?: ImpactColumnExperienceType): string {
  if (type === "Location") return "Text";
  return type || "Text";
}

export function experienceToPageContent(
  type: ImpactColumnExperienceType | undefined,
  experience: ImpactColumnExperience | undefined,
  extra: Record<string, unknown>
): Record<string, unknown> {
  const title = String(extra.title || experience?.title || "");
  const description = String(extra.description || "");
  const url = experience?.url ?? "";
  const images = columnImageUrls(experience);
  const shared = {
    ...extra,
    ...(title ? { title } : {}),
    ...(description ? { description } : {}),
    experience_title: experience?.title ?? "",
    experience_description: experience?.description ?? "",
  };

  if (type === "Image") {
    return { ...shared, url: images };
  }
  if (type === "Video") {
    return { ...shared, url };
  }
  if (type === "Audio") {
    return { ...shared, url };
  }
  if (type === "Youtube") {
    const youtubeUrl =
      url ||
      (typeof experience?.title === "string" && /youtu(\.be|be\.com)/i.test(experience.title)
        ? experience.title
        : "");
    return { ...shared, url: youtubeUrl };
  }
  if (type === "Location") {
    return { ...shared, address: experience?.address ?? "", url };
  }
  return shared;
}

function buildPageEntry(input: {
  order: number;
  type: string;
  content: Record<string, unknown>;
  parentId?: number;
}): AddMainCardPageEntry {
  const parentId = Number(input.parentId);
  const hasParent = Number.isFinite(parentId) && parentId > 0;
  return {
    order: input.order,
    type: input.type,
    content: input.content,
    tray_id: 0,
    sub_pages: [],
    ...(hasParent ? { parent_id: parentId } : {}),
  };
}

export function buildAddCubePayload(input: {
  mainCardId: number;
  order: number;
  title: string;
  description?: string;
  thumbnailUrl?: string;
  cubeId: string;
  experienceType?: ImpactColumnExperienceType;
  experience?: ImpactColumnExperience;
  sectionKey?: string;
}): AddMainCardPagesPayload {
  const type = experienceToPageType(input.experienceType);
  const images = columnImageUrls(input.experience);
  return {
    main_card_id: input.mainCardId,
    pages: [
      buildPageEntry({
        order: input.order,
        type,
        content: experienceToPageContent(input.experienceType, input.experience, {
          title: input.title,
          description: input.description ?? "",
          thumbnail_url: input.thumbnailUrl || images[0] || "",
          [IMPACT_ANCHOR_FLAG]: true,
          cube_id: input.cubeId,
          ...(input.sectionKey ? { __impact_section: input.sectionKey } : {}),
        }),
      }),
    ],
  };
}

export function buildCubePatchBody(input: {
  cubeId: string;
  title: string;
  description?: string;
  thumbnailUrl?: string;
  experienceType?: ImpactColumnExperienceType;
  experience?: ImpactColumnExperience;
}) {
  const images = columnImageUrls(input.experience);
  return {
    type: experienceToPageType(input.experienceType),
    content: experienceToPageContent(input.experienceType, input.experience, {
      title: input.title,
      description: input.description ?? "",
      thumbnail_url: input.thumbnailUrl || images[0] || "",
      [IMPACT_ANCHOR_FLAG]: true,
      cube_id: input.cubeId,
    }),
  };
}

export function buildAddSubpagePayload(input: {
  mainCardId: number;
  parentPageId: number;
  order: number;
  cubeId: string;
  columnId: string;
  title: string;
  description?: string;
  experienceType?: ImpactColumnExperienceType;
  experience?: ImpactColumnExperience;
  sectionKey?: string;
}): AddMainCardPagesPayload {
  const type = experienceToPageType(input.experienceType);
  const content = experienceToPageContent(input.experienceType, input.experience, {
    title: input.title,
    description: input.description ?? "",
    [IMPACT_SUBPAGE_FLAG]: true,
    parent_id: input.parentPageId,
    cube_id: input.cubeId,
    column_id: input.columnId,
    ...(input.sectionKey ? { __impact_section: input.sectionKey } : {}),
  });

  return {
    main_card_id: input.mainCardId,
    pages: [
      buildPageEntry({
        order: input.order,
        type,
        parentId: input.parentPageId,
        content,
      }),
    ],
  };
}

export function buildSubpagePatchBody(input: {
  parentPageId?: number;
  cubeId: string;
  columnId: string;
  title: string;
  description?: string;
  experienceType?: ImpactColumnExperienceType;
  experience?: ImpactColumnExperience;
  sectionKey?: string;
}) {
  const type = experienceToPageType(input.experienceType);
  return {
    type,
    content: experienceToPageContent(input.experienceType, input.experience, {
      title: input.title,
      description: input.description ?? "",
      [IMPACT_SUBPAGE_FLAG]: true,
      parent_id: input.parentPageId,
      cube_id: input.cubeId,
      column_id: input.columnId,
      ...(input.sectionKey ? { __impact_section: input.sectionKey } : {}),
    }),
  };
}

export function extractCreatedPages(response: unknown): ImpactApiPage[] {
  const envelope = (response && typeof response === "object" ? response : {}) as Record<string, unknown>;
  const data = (envelope.data && typeof envelope.data === "object" ? envelope.data : envelope) as Record<
    string,
    unknown
  >;
  const nested = data.data && typeof data.data === "object" ? (data.data as Record<string, unknown>) : data;

  const lists = [data.pages, nested.pages, data, nested, envelope];
  for (const list of lists) {
    if (Array.isArray(list) && list.length && typeof list[0] === "object") {
      return list as ImpactApiPage[];
    }
  }

  if (Number(nested.id) > 0 || Number(data.id) > 0) {
    return [nested as ImpactApiPage];
  }
  return [];
}

export function extractCreatedPageId(response: unknown): number | undefined {
  const pages = extractCreatedPages(response);
  const listed = Number(pages[0]?.id);
  if (Number.isFinite(listed) && listed > 0) return listed;

  const seen = new Set<unknown>();
  const walk = (value: unknown): number | undefined => {
    if (!value || typeof value !== "object" || seen.has(value)) return undefined;
    seen.add(value);
    if (Array.isArray(value)) {
      for (const item of value) {
        const found = walk(item);
        if (found) return found;
      }
      return undefined;
    }
    const rec = value as Record<string, unknown>;
    const candidate = Number(rec.id);
    if (
      Number.isFinite(candidate) &&
      candidate > 0 &&
      (rec.type != null || rec.content != null || rec.order != null || rec.main_card_id != null)
    ) {
      return candidate;
    }
    for (const key of ["data", "pages", "page", "result", "payload"]) {
      const found = walk(rec[key]);
      if (found) return found;
    }
    return undefined;
  };

  return walk(response);
}

function contentOf(page: ImpactApiPage): Record<string, unknown> {
  return page?.content && typeof page.content === "object" ? (page.content as Record<string, unknown>) : {};
}

function firstMediaUrl(value: unknown): string {
  if (typeof value === "string" && value.trim()) return value.trim();
  if (Array.isArray(value)) {
    const first = value.find((item) => typeof item === "string" && item.trim());
    return typeof first === "string" ? first.trim() : "";
  }
  return "";
}

function looksLikeYoutubeUrl(value?: string): boolean {
  return Boolean(value && /youtu(\.be|be\.com)/i.test(value));
}

function pageExperience(page: ImpactApiPage): {
  type?: ImpactColumnExperienceType;
  experience: ImpactColumnExperience;
  thumbnailUrl?: string;
} {
  const content = contentOf(page);
  const rawType = String(page.type ?? content.experienceType ?? "") as ImpactColumnExperienceType;
  const images = Array.isArray(content.images)
    ? (content.images as string[])
    : Array.isArray(content.url)
      ? (content.url as string[])
      : [];
  const experienceTitle = String(content.experience_title ?? "");
  const url =
    firstMediaUrl(content.url) ||
    firstMediaUrl(content.videoUrl) ||
    firstMediaUrl(content.audioUrl) ||
    (rawType === "Youtube" && looksLikeYoutubeUrl(experienceTitle) ? experienceTitle : "");
  const hasContentFields =
    "experience_description" in content ||
    "experience_title" in content ||
    images.length > 0 ||
    Boolean(url) ||
    Boolean(content.address);
  const type = IMPACT_COLUMN_EXPERIENCE_META[rawType] && (rawType !== "Text" || hasContentFields) ? rawType : undefined;
  return {
    type,
    thumbnailUrl: String(content.thumbnail_url || images[0] || "") || undefined,
    experience: {
      title: looksLikeYoutubeUrl(experienceTitle) ? "" : experienceTitle,
      description: String(
        content.experience_description ?? ("experience_description" in content ? "" : content.description ?? "")
      ),
      url,
      urls: images,
      address: String(content.address || ""),
    },
  };
}

export function cubeFromSubpages(parentId?: number, pages?: ImpactApiPage[] | null): AnchorCube | undefined {
  const id = Number(parentId);
  if (!Number.isFinite(id) || id <= 0) return undefined;
  const children = flattenImpactPages(pages).filter((page) => parentPageIdOf(page) === id);
  if (!children.length) return undefined;
  return {
    id: `subpages-${id}`,
    title: "",
    tone: "coral",
    columns: uniqueColumns(children.map(pageToColumn)),
  };
}

export function uniqueColumns(columns: AnchorCubeColumn[]): AnchorCubeColumn[] {
  const seen = new Set<string>();
  const out: AnchorCubeColumn[] = [];
  for (const column of columns) {
    const key = column.pageId ? `page:${column.pageId}` : `id:${column.id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(column);
  }
  return out;
}

export function pageToColumn(page: ImpactApiPage): AnchorCubeColumn {
  const content = contentOf(page);
  const parsed = pageExperience(page);
  return {
    id: String(content.column_id || `col-page-${page.id}`),
    pageId: pageIdOf(page),
    title: String(content.title || "Untitled"),
    description: String(content.description || "") || undefined,
    icon: parsed.type ? IMPACT_COLUMN_EXPERIENCE_META[parsed.type].icon : "doc",
    experienceType: parsed.type ?? "Text",
    experience: parsed.experience,
    sectionKey: content.__impact_section as AnchorCubeColumn["sectionKey"],
    verified: isImpactPageVerified(page),
    reviewStatus: readImpactPageReviewStatus(page),
    creator: String(page.creator || content.creator || ""),
  };
}

function columnsForCube(
  cubeId: string,
  pageId: number | undefined,
  subpages: ImpactApiPage[],
  byParent: Map<number, ImpactApiPage[]>
) {
  const fromParent = pageId ? byParent.get(pageId) ?? [] : [];
  const fromCube = subpages.filter((page) => String(contentOf(page).cube_id || "") === cubeId);
  const seen = new Set<string>();
  const mergedPages: ImpactApiPage[] = [];
  for (const page of [...fromParent, ...fromCube]) {
    const key = String(pageIdOf(page) || contentOf(page).column_id || page.id || "");
    if (!key || seen.has(key)) continue;
    seen.add(key);
    mergedPages.push(page);
  }
  return mergedPages.map((page) => {
    const column = pageToColumn(page);
    const uniqueId = pageIdOf(page) ? `col-page-${pageIdOf(page)}` : column.id;
    return { ...column, id: uniqueId };
  });
}

export function mergeCubesWithApiPages(stored: AnchorCube[] | null, pages: ImpactApiPage[]): AnchorCube[] {
  const flatPages = flattenImpactPages(pages);
  const base = ensureDefaultImpactCubes(stored);
  const anchors = flatPages.filter(isImpactAnchorPage);
  const subpages = flatPages.filter(isImpactSubpage);
  const shellId = findImpactShellPageId(flatPages);

  const byParent = new Map<number, ImpactApiPage[]>();
  for (const page of subpages) {
    const parentId = parentPageIdOf(page);
    if (!parentId) continue;
    const list = byParent.get(parentId) ?? [];
    list.push(page);
    byParent.set(parentId, list);
  }

  const usedAnchorIds = new Set<number>();

  const merged = base.map((cube) => {
    if (cube.locked) {
      const extras = subpages.filter((page) => {
        const content = contentOf(page);
        const cubeId = String(content.cube_id || "");
        const columnId = String(content.column_id || "");
        if (cubeId && cubeId === cube.id) return true;
        return Boolean(columnId && cube.columns.some((column) => column.id === columnId));
      });
      const extraColumns = extras
        .map(pageToColumn)
        .filter((column) => !LEGACY_RECIPIENT_MEDIA_COLUMN_IDS.has(column.id));
      const leftover = new Map(extraColumns.map((column) => [column.id, column]));
      const updated = cube.columns.map((column) => {
        const fromApi = leftover.get(column.id);
        if (!fromApi) return column;
        leftover.delete(column.id);
        const titleLooksLikeExperience =
          Boolean(column.sectionKey) &&
          Boolean(fromApi.experience?.title) &&
          fromApi.title === fromApi.experience?.title;
        return {
          ...column,
          ...fromApi,
          id: column.id,
          sectionKey: column.sectionKey,
          title: titleLooksLikeExperience ? column.title : fromApi.title || column.title,
          description: fromApi.description || column.description,
        };
      });
      return {
        ...cube,
        pageId: cube.pageId ?? shellId,
        columns: [...updated, ...extraColumns.filter((column) => leftover.has(column.id))],
      };
    }

    const match =
      (cube.pageId ? anchors.find((page) => Number(page.id) === cube.pageId) : undefined) ||
      anchors.find((page) => String(contentOf(page).cube_id || "") === cube.id);

    if (!match?.id) return cube;
    usedAnchorIds.add(Number(match.id));
    const content = contentOf(match);
    const apiColumns = uniqueColumns(columnsForCube(cube.id, Number(match.id), subpages, byParent));
    const parsed = pageExperience(match);
    return {
      ...cube,
      pageId: Number(match.id),
      creator: String(match.creator || content.creator || cube.creator || ""),
      title: String(content.title || cube.title),
      description: String(content.description || cube.description || "") || cube.description,
      thumbnailUrl: parsed.thumbnailUrl || cube.thumbnailUrl,
      experienceType: parsed.type ?? cube.experienceType,
      experience: parsed.experience ?? cube.experience,
      icon: parsed.type ? IMPACT_COLUMN_EXPERIENCE_META[parsed.type].icon : cube.icon,
      columns: apiColumns.length ? apiColumns : cube.columns,
    };
  });

  const extraCubes = anchors
    .filter((page) => page.id && !usedAnchorIds.has(Number(page.id)))
    .map((page, index) => {
      const content = contentOf(page);
      const parsed = pageExperience(page);
      const cubeId = String(content.cube_id || `cube-page-${page.id}`);
      return {
        id: cubeId,
        pageId: pageIdOf(page),
        title: String(content.title || "Untitled"),
        description: String(content.description || "") || undefined,
        thumbnailUrl: parsed.thumbnailUrl,
        experienceType: parsed.type,
        experience: parsed.experience,
        icon: parsed.type ? IMPACT_COLUMN_EXPERIENCE_META[parsed.type].icon : "cube",
        tone: DEFAULT_IMPACT_ANCHOR_CUBES[index % DEFAULT_IMPACT_ANCHOR_CUBES.length]?.tone ?? "coral",
        collapsed: true,
        columns: uniqueColumns(columnsForCube(cubeId, pageIdOf(page), subpages, byParent)),
        sectionKey: content.__impact_section as AnchorCube["sectionKey"],
        creator: String(page.creator || content.creator || ""),
        verified: isImpactPageVerified(page),
        reviewStatus: readImpactPageReviewStatus(page),
      } as AnchorCube;
    });

  return [...merged, ...extraCubes];
}
